import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Loader2, AlertCircle, ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { runPlateView } from '../api/brainer'
import { PlateViewRenderer, PreparationTriggerView, EmptyState, getByDotPath, usePlateNavigateTo, buildPlateUrl, DEFAULT_JOURNEY_API, COMPACT_VARIANT_TEXT, type JourneyApi } from './PlateViewRenderer'
import { BUTTON_ICONS } from '../pages/BrainerPlateDetail'
import { Tabs, TabsList, TabsTrigger } from './ui/tabs'
import type { BPlateDetail, BPlateSectionButton, BPlateSectionRow, BPlateView, BPlateViewPaginationConfig, BPlateViewSearchConfig } from '../types'

type RunViewFn = (viewId: string, inputs: Record<string, string>) => Promise<{ data: unknown; error?: string }>

interface PlateLayoutProps {
  plate: BPlateDetail
  runInputs: Record<string, string>
  onParamUpdate?: (updates: Record<string, string>) => void
  // Defaults to the authenticated runPlateView call. The public OpenPlace token page passes a
  // token-scoped variant instead — everything else about rendering stays identical.
  runView?: RunViewFn
  // Same defaulting pattern as runView, for the trigger/poll calls a preparation-category view,
  // a button view, or a table row action makes.
  journeyApi?: JourneyApi
}

const SECTION_GAP_PX = 24 // matches the gap-6 on the row below (TailAdmin grid gap)

export function PlateLayout({ plate, runInputs, onParamUpdate, runView, journeyApi = DEFAULT_JOURNEY_API }: PlateLayoutProps) {
  const sorted = [...plate.sections].sort((a, b) => a.rank - b.rank)
  const navigate = useNavigate()
  // N equal-width sections share (N-1) gaps of SECTION_GAP_PX between them — each section's
  // width must give back its share of that gap space, or N sections whose widths sum to exactly
  // 100% overflow the row by (N-1)*SECTION_GAP_PX and wrap early. A fixed "-6px" (hardcoded for
  // the original 2-section case, where 1 gap split across 2 items is exactly 6px) only balances
  // for N=2 — e.g. 4 sections at 25% each overflowed by 12px and wrapped to two rows.
  const widthReductionPx = sorted.length > 0 ? ((sorted.length - 1) * SECTION_GAP_PX) / sorted.length : 0

  return (
    <div className="flex gap-6 items-start flex-wrap">
      {sorted.map(section => (
        <section
          key={section.id}
          style={{ width: `calc(${section.width}% - ${widthReductionPx}px)`, minWidth: '160px' }}
          className="flex-shrink-0 space-y-4"
        >
          {(section.title || (section.buttons?.length ?? 0) > 0) && (
            <div className="section-head">
              <h2 className="text-xl font-semibold text-gray-800">{section.title ?? ''}</h2>
              {(section.buttons?.length ?? 0) > 0 && (
                <div className="flex flex-wrap items-center gap-3">
                  {[...(section.buttons ?? [])].sort((a, b) => a.position - b.position).map(btn => (
                    <SectionButton key={btn.id} btn={btn} onNavigate={navigate} runInputs={runInputs} journeyApi={journeyApi} />
                  ))}
                </div>
              )}
            </div>
          )}
          <SectionViews views={section.views ?? []} rows={section.rows ?? []} runInputs={runInputs} onParamUpdate={onParamUpdate} runView={runView} journeyApi={journeyApi} />
        </section>
      ))}
    </div>
  )
}

// Each row is self-contained: a plain row (see PlateSectionRow) renders its views side by side
// (flex-1 each, sharing the row's width evenly); a tab row (row.isTabbed) renders its views as an
// independent tab strip instead, one tab per view ordered by the view's own rank (rank 0 defaults
// active). Two tab rows in the same section render as two independent strips.
function SectionViews({ views, rows, runInputs, onParamUpdate, runView, journeyApi }: {
  views: BPlateView[]
  rows: BPlateSectionRow[]
  runInputs: Record<string, string>
  onParamUpdate?: (updates: Record<string, string>) => void
  runView?: RunViewFn
  journeyApi: JourneyApi
}) {
  const sortedRows = [...rows].sort((a, b) => a.rank - b.rank)
  const byRow = sortedRows
    .map(row => ({ row, views: views.filter(v => v.plateSectionRowId === row.id).sort((a, b) => a.rank - b.rank) }))
    .filter(r => r.views.length > 0)

  return (
    <>
      {byRow.map(({ row, views: rowViews }) => (
        row.isTabbed
          ? <TabRow key={row.id} views={rowViews} runInputs={runInputs} onParamUpdate={onParamUpdate} runView={runView} journeyApi={journeyApi} />
          : (
            <div key={row.id} className="section-row">
              {rowViews.map(view => (
                <div key={view.id} className="flex-1 min-w-0">
                  <PlateViewPanel view={view} runInputs={runInputs} onParamUpdate={onParamUpdate} runView={runView} journeyApi={journeyApi} />
                </div>
              ))}
            </div>
          )
      ))}
    </>
  )
}

// A dedicated component per tab row so each strip gets its own independent activeTabId state —
// React's rules of hooks don't allow useState calls varying per iteration inside a plain .map().
function TabRow({ views, runInputs, onParamUpdate, runView, journeyApi }: {
  views: BPlateView[]
  runInputs: Record<string, string>
  onParamUpdate?: (updates: Record<string, string>) => void
  runView?: RunViewFn
  journeyApi: JourneyApi
}) {
  const [activeTabId, setActiveTabId] = useState<string | undefined>(views[0]?.id)
  const activeTab = views.find(v => v.id === activeTabId) ?? views[0]

  return (
    <Tabs value={activeTab?.id ?? ''} onValueChange={setActiveTabId}>
      <TabsList className="mb-4">
        {views.map(view => (
          <TabsTrigger key={view.id} value={view.id}>{view.title}</TabsTrigger>
        ))}
      </TabsList>
      {activeTab && (
        <PlateViewPanel key={activeTab.id} view={activeTab} runInputs={runInputs} onParamUpdate={onParamUpdate} runView={runView} journeyApi={journeyApi} inTabRow />
      )}
    </Tabs>
  )
}

// A section-header button either navigates to another Plate (destinationPlateId — through
// buildPlateUrl, so it stays inside whichever workspace shell is rendering this Plate, Garage included)
// or triggers a Preparation (preparationId — new: opens the same button+modal flow a
// 'preparation'-category view uses, via usePlateNavigateTo so the modal's own forwarding, if any,
// stays inside whichever workspace shell this Plate is currently rendered under). This is what
// lets a "+ Nouveau"-style create action sit next to the section title instead of permanently
// occupying its own column.
//
// compact=true renders the same button discretely (icon-over-label, muted/variant-colored text,
// no filled background) — used for a Plate's global buttons, rendered in a workspace navbar
// rather than a section header row (see GlobalButtonsBar below).
function SectionButton({ btn, onNavigate, runInputs, journeyApi, compact }: {
  btn: BPlateSectionButton
  onNavigate: (path: string) => void
  runInputs: Record<string, string>
  journeyApi: JourneyApi
  compact?: boolean
}) {
  const { navigateTo, isOpenPlace } = usePlateNavigateTo()
  const location = useLocation()
  const IconComp = btn.icon ? BUTTON_ICONS[btn.icon] : undefined
  const cls = btn.variant ? `btn-${btn.variant}` : 'btn-secondary'

  if (btn.preparationId) {
    return (
      <PreparationTriggerView
        title={btn.label}
        preparationId={btn.preparationId}
        runInputs={runInputs}
        navigateTo={navigateTo}
        isOpenPlace={isOpenPlace}
        journeyApi={journeyApi}
        compact={compact}
        icon={IconComp}
        variant={btn.variant}
      />
    )
  }

  if (compact) {
    return (
      <button
        onClick={() => {
          if (!btn.destinationPlateId) return
          const qs = new URLSearchParams(runInputs).toString()
          onNavigate(buildPlateUrl(location.pathname, isOpenPlace, btn.destinationPlateId, qs ? `?${qs}` : ''))
        }}
        disabled={!btn.destinationPlateId}
        className={`flex flex-col items-center gap-1 rounded-lg px-2.5 py-1.5 hover:bg-gray-100 transition-colors disabled:opacity-40 ${COMPACT_VARIANT_TEXT[btn.variant ?? ''] ?? 'text-ink-dim'}`}
        title={btn.label}
      >
        {IconComp && <IconComp size={15} />}
        <span className="text-theme-xs leading-none font-medium whitespace-nowrap">{btn.label}</span>
      </button>
    )
  }

  return (
    <button
      onClick={() => {
        if (!btn.destinationPlateId) return
        const qs = new URLSearchParams(runInputs).toString()
        onNavigate(buildPlateUrl(location.pathname, isOpenPlace, btn.destinationPlateId, qs ? `?${qs}` : ''))
      }}
      disabled={!btn.destinationPlateId}
      className={`${cls} disabled:opacity-40 flex items-center gap-1.5`}
    >
      {IconComp && <IconComp size={13} />}
      {btn.label}
    </button>
  )
}

// A Plate's own global buttons (plateSectionId === null) — rendered in the owning workspace's
// navbar (see GarageDashboardLayout.tsx / OpenPlaceLayout.tsx) rather than any one section's
// header row. Always compact/discrete, per the "global buttons are minimalist by default" spec.
export function GlobalButtonsBar({ buttons, runInputs, journeyApi = DEFAULT_JOURNEY_API }: {
  buttons: BPlateSectionButton[]
  runInputs: Record<string, string>
  journeyApi?: JourneyApi
}) {
  const navigate = useNavigate()
  if (!buttons.length) return null
  return (
    <div className="flex items-center gap-1">
      {[...buttons].sort((a, b) => a.position - b.position).map(btn => (
        <SectionButton key={btn.id} btn={btn} onNavigate={navigate} runInputs={runInputs} journeyApi={journeyApi} compact />
      ))}
    </div>
  )
}

interface PlateViewPanelProps {
  view: BPlateView
  runInputs: Record<string, string>
  onParamUpdate?: (updates: Record<string, string>) => void
  runView?: RunViewFn
  journeyApi?: JourneyApi
  // Set by TabRow only — a tab strip's own chrome already separates its panes, so a non-card view
  // (table/chart/etc.) inside one doesn't need its own redundant bordered/shadowed box on top.
  inTabRow?: boolean
}

export function PlateViewPanel({ view, runInputs, onParamUpdate, runView, journeyApi, inTabRow }: PlateViewPanelProps) {
  const isPreparation = view.category === 'preparation'
  // Button/form views have no drinkerId/inputParamId — runView() always returns {data: null}
  // for them (see plate.use-cases.ts), so running the query is wasted work; PlateViewRenderer
  // special-cases both before it would ever look at this query's result anyway.
  const isButton = view.category === 'button' || view.category === 'form'
  const doRunView = runView ?? ((viewId, inputs) => runPlateView(viewId, inputs).then(r => r.data))

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['plate-view-run', view.id, runInputs],
    queryFn: () => doRunView(view.id, runInputs),
    enabled: !isPreparation && !isButton,
    retry: false,
    staleTime: Infinity,
  })

  // Apply output mappings to feed result values back as input params.
  // Use a ref so the effect always sees the latest runInputs without re-running.
  const runInputsRef = useRef(runInputs)
  runInputsRef.current = runInputs
  useEffect(() => {
    if (!data?.data || !view.outputMappings?.length || !onParamUpdate) return
    const pageParam = view.paginationConfig?.pageParamName
    const updates: Record<string, string> = {}
    for (const m of view.outputMappings) {
      if (pageParam && m.toParamName === pageParam) continue  // pagination bar owns this param
      const val = getByDotPath(data.data as Record<string, unknown>, m.fromPath)
      if (val !== undefined && val !== null) updates[m.toParamName] = String(val)
    }
    if (!Object.keys(updates).length) return
    if (Object.entries(updates).every(([k, v]) => runInputsRef.current[k] === v)) return
    onParamUpdate(updates)
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const rawData = data?.data as Record<string, unknown> | undefined
  // A card view whose columns are nav destinations (isLink) already renders each entry as its
  // own bordered tile (CardView's nav-tile mode) — wrapping that grid in another card, with a
  // header repeating a title the workspace navbar/section title likely already shows, is a
  // useless extra layer (confirmed against a real Home page: "Garage" shown 3 times in a row).
  // Skip this Plate's own card/header for that case, on every Plate, not just Home.
  const isNavHub = view.category === 'card' && (view.columnConfig?.some(c => c.isLink) ?? false)
  // A tab strip's own chrome already separates its panes — a non-card view inside one (table,
  // chart, etc.) doesn't need its own redundant bordered/shadowed box on top of that.
  const suppressCard = isNavHub || (inTabRow && view.category !== 'card')
  // Unset (never configured in the wizard) always means "render exactly as before this feature
  // existed" — title/description visible, default px-5/py-4 spacing — so existing Plates never
  // change appearance just because this shipped. Only an explicit saved value overrides that.
  const titleVisible = view.showTitle ?? true
  const descVisible = (view.showDescription ?? true) && !!view.description
  const showHeader = !isPreparation && !isButton && !isNavHub && (titleVisible || descVisible)
  const sp = view.spacing

  return (
    <div
      className={suppressCard ? '' : 'plate-card overflow-hidden'}
      style={{
        ...(view.backgroundColor && { backgroundColor: view.backgroundColor }),
        ...(view.borderColor     && { borderColor: view.borderColor }),
        ...(view.borderStyle     && { borderStyle: view.borderStyle }),
        ...(view.borderWidth     && { borderWidth: view.borderWidth }),
        ...(view.borderRadius    && { borderRadius: view.borderRadius }),
      }}
    >
      {showHeader && (
        <div className={suppressCard ? 'pb-4' : 'px-6 pt-5'}>
          {titleVisible && <h3 className="text-base font-medium text-gray-800">{view.title}</h3>}
          {descVisible && <p className="mt-1 text-sm text-gray-500">{view.description}</p>}
        </div>
      )}
      {view.searchConfig && (
        <SearchBar
          config={view.searchConfig}
          currentValue={runInputs[view.searchConfig.searchParamName] ?? ''}
          onSearch={val => onParamUpdate?.({
            [view.searchConfig!.searchParamName]: val,
            ...(view.paginationConfig ? { [view.paginationConfig.pageParamName]: '1' } : {}),
          })}
        />
      )}
      <div
        className={isNavHub || (suppressCard && !sp) ? '' : sp ? undefined : 'px-6 py-5'}
        style={sp ? {
          paddingTop: `${sp.paddingTop * 0.25}rem`, paddingRight: `${sp.paddingRight * 0.25}rem`,
          paddingBottom: `${sp.paddingBottom * 0.25}rem`, paddingLeft: `${sp.paddingLeft * 0.25}rem`,
          marginTop: `${sp.marginTop * 0.25}rem`, marginRight: `${sp.marginRight * 0.25}rem`,
          marginBottom: `${sp.marginBottom * 0.25}rem`, marginLeft: `${sp.marginLeft * 0.25}rem`,
        } : undefined}
      >
        {isPreparation || isButton ? (
          <PlateViewRenderer result={{ view, data: null, error: undefined }} runInputs={runInputs} journeyApi={journeyApi} />
        ) : isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 size={20} className="animate-spin text-ink" />
          </div>
        ) : isError ? (
          <div className="flex items-center gap-1.5 text-xs text-red-500">
            <AlertCircle size={12} />
            {(error as any)?.response?.data?.error ?? 'Failed to load'}
          </div>
        ) : data ? (
          <PlateViewRenderer result={{ view, data: data.data, error: data.error }} runInputs={runInputs} journeyApi={journeyApi} />
        ) : (
          <EmptyState message="No data yet" />
        )}
      </div>
      {view.paginationConfig && rawData && (
        <PaginationBar
          rawData={rawData}
          config={view.paginationConfig}
          onPageChange={page => onParamUpdate?.({ [view.paginationConfig!.pageParamName]: String(page) })}
        />
      )}
    </div>
  )
}

// ── Pagination bar ─────────────────────────────────────────────────────────────

function PaginationBar({ rawData, config, onPageChange }: {
  rawData: Record<string, unknown>
  config: BPlateViewPaginationConfig
  onPageChange: (page: number) => void
}) {
  const currentPage = Number(getByDotPath(rawData, config.currentPagePath)) || 1
  const totalPages  = Number(getByDotPath(rawData, config.totalPagesPath))  || 1
  // Show up to 5 page buttons centred around currentPage
  const pages: number[] = []
  const half = 2
  let start = Math.max(1, currentPage - half)
  let end   = Math.min(totalPages, start + 4)
  start = Math.max(1, end - 4)
  for (let p = start; p <= end; p++) pages.push(p)

  return (
    // TailAdmin Pagination: bordered Previous/Next, 40px rounded-lg page buttons, brand-500 current.
    <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-6 py-4">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className={PAGER_EDGE_BTN}
      >
        <ChevronLeft size={20} /> <span className="hidden sm:inline">Previous</span>
      </button>

      <div className="flex items-center gap-1">
        {start > 1 && (
          <>
            <button onClick={() => onPageChange(1)} className={pagerPageBtn(false)}>1</button>
            {start > 2 && <span className="px-1 text-sm text-gray-500">…</span>}
          </>
        )}

        {pages.map(p => (
          <button key={p} onClick={() => onPageChange(p)} className={pagerPageBtn(p === currentPage)}>
            {p}
          </button>
        ))}

        {end < totalPages && (
          <>
            {end < totalPages - 1 && <span className="px-1 text-sm text-gray-500">…</span>}
            <button onClick={() => onPageChange(totalPages)} className={pagerPageBtn(false)}>{totalPages}</button>
          </>
        )}
      </div>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className={PAGER_EDGE_BTN}
      >
        <span className="hidden sm:inline">Next</span> <ChevronRight size={20} />
      </button>
    </div>
  )
}

const PAGER_EDGE_BTN = 'flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
function pagerPageBtn(active: boolean) {
  return `flex h-10 min-w-[40px] items-center justify-center rounded-lg px-2 text-sm font-medium transition-colors ${
    active ? 'bg-brand-500 text-white' : 'text-gray-700 hover:bg-brand-500/[0.08] hover:text-brand-500'
  }`
}

// ── Search bar ──────────────────────────────────────────────────────────────────

function SearchBar({ config, currentValue, onSearch }: {
  config: BPlateViewSearchConfig
  currentValue: string
  onSearch: (value: string) => void
}) {
  const [text, setText] = useState(currentValue)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => onSearch(text), 400)
    return () => clearTimeout(debounceRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text])

  return (
    <div className="px-6 pt-5">
      <div className="relative">
        <Search size={20} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={config.placeholder ?? 'Search...'}
          className="field pl-12"
        />
      </div>
    </div>
  )
}
